import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Camera, Trash2, X, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

export const ScreenshotGallery = ({ timeEntryId, isOpen, onClose }) => {
  const { api, token } = useAuth();
  const [screenshots, setScreenshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [imageUrls, setImageUrls] = useState({}); // Cache blob URLs
  const [loadingImages, setLoadingImages] = useState({});

  useEffect(() => {
    if (isOpen && timeEntryId) {
      fetchScreenshots();
    }
    return () => {
      // Cleanup blob URLs when component unmounts
      Object.values(imageUrls).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [isOpen, timeEntryId]);

  const fetchScreenshots = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/screenshots/time-entry/${timeEntryId}`);
      setScreenshots(response.data);
      console.log('Fetched screenshots:', response.data);
    } catch (error) {
      console.error('Failed to fetch screenshots:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch image with authentication and convert to blob URL
  const fetchImageUrl = useCallback(async (screenshotId) => {
    if (imageUrls[screenshotId]) {
      return imageUrls[screenshotId];
    }

    setLoadingImages(prev => ({ ...prev, [screenshotId]: true }));

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/screenshots/${screenshotId}/image`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      setImageUrls(prev => ({ ...prev, [screenshotId]: url }));
      return url;
    } catch (error) {
      console.error('Failed to fetch image:', error);
      return null;
    } finally {
      setLoadingImages(prev => ({ ...prev, [screenshotId]: false }));
    }
  }, [token, imageUrls]);

  // Preload images when screenshots are loaded
  useEffect(() => {
    if (screenshots.length > 0) {
      // Preload first few images
      screenshots.slice(0, 4).forEach(s => fetchImageUrl(s.id));
    }
  }, [screenshots, fetchImageUrl]);

  const handleDelete = async (screenshotId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/screenshots/${screenshotId}`);
      setScreenshots(prev => prev.filter(s => s.id !== screenshotId));
      // Cleanup blob URL
      if (imageUrls[screenshotId]) {
        URL.revokeObjectURL(imageUrls[screenshotId]);
        setImageUrls(prev => {
          const newUrls = { ...prev };
          delete newUrls[screenshotId];
          return newUrls;
        });
      }
    } catch (error) {
      console.error('Failed to delete screenshot:', error);
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const navigateImage = (direction) => {
    if (direction === 'prev' && selectedIndex > 0) {
      const newIndex = selectedIndex - 1;
      setSelectedIndex(newIndex);
      fetchImageUrl(screenshots[newIndex].id);
    } else if (direction === 'next' && selectedIndex < screenshots.length - 1) {
      const newIndex = selectedIndex + 1;
      setSelectedIndex(newIndex);
      fetchImageUrl(screenshots[newIndex].id);
    }
  };

  const handleSelectImage = (index) => {
    setSelectedIndex(index);
    fetchImageUrl(screenshots[index].id);
  };

  // Image component with loading state
  const ScreenshotImage = ({ screenshotId, className, alt }) => {
    const [localUrl, setLocalUrl] = useState(imageUrls[screenshotId]);
    const isLoading = loadingImages[screenshotId];

    useEffect(() => {
      if (!localUrl && !isLoading) {
        fetchImageUrl(screenshotId).then(url => setLocalUrl(url));
      }
    }, [screenshotId, localUrl, isLoading]);

    useEffect(() => {
      setLocalUrl(imageUrls[screenshotId]);
    }, [imageUrls[screenshotId]]);

    if (isLoading || !localUrl) {
      return (
        <div className={`flex items-center justify-center bg-slate-100 dark:bg-slate-800 ${className}`}>
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      );
    }

    return (
      <img
        src={localUrl}
        alt={alt}
        className={className}
      />
    );
  };

  return (
    <>
      {/* Main Gallery Dialog */}
      <Dialog open={isOpen && selectedIndex === null} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-[#00A3E0]" />
              Screenshots ({screenshots.length})
            </DialogTitle>
          </DialogHeader>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#00A3E0]" />
            </div>
          ) : screenshots.length === 0 ? (
            <div className="text-center py-12">
              <Camera className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-500">No screenshots captured for this entry</p>
              <p className="text-sm text-slate-400 mt-2">Screenshots are captured automatically every minute while the timer is running</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 overflow-y-auto max-h-[60vh] p-2">
              {screenshots.map((screenshot, index) => (
                <div
                  key={screenshot.id}
                  className="relative group cursor-pointer rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 hover:border-[#00A3E0] transition-colors"
                  onClick={() => handleSelectImage(index)}
                  data-testid={`screenshot-thumbnail-${screenshot.id}`}
                >
                  <div className="aspect-video bg-slate-100 dark:bg-slate-800">
                    <ScreenshotImage
                      screenshotId={screenshot.id}
                      alt={`Screenshot at ${formatTime(screenshot.captured_at)}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <p className="text-xs text-white">{formatTime(screenshot.captured_at)}</p>
                  </div>
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 w-7 h-7 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => handleDelete(screenshot.id, e)}
                    data-testid={`delete-screenshot-${screenshot.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Full Image Viewer */}
      {selectedIndex !== null && (
        <div 
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={() => setSelectedIndex(null)}
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white dark:bg-slate-800/10 z-10"
            onClick={() => setSelectedIndex(null)}
          >
            <X className="w-6 h-6" />
          </Button>
          
          {selectedIndex > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 text-white hover:bg-white dark:bg-slate-800/10 z-10"
              onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }}
            >
              <ChevronLeft className="w-8 h-8" />
            </Button>
          )}
          
          {selectedIndex < screenshots.length - 1 && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 text-white hover:bg-white dark:bg-slate-800/10 z-10"
              onClick={(e) => { e.stopPropagation(); navigateImage('next'); }}
            >
              <ChevronRight className="w-8 h-8" />
            </Button>
          )}
          
          <div className="max-w-[90vw] max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            {loadingImages[screenshots[selectedIndex]?.id] ? (
              <div className="flex items-center justify-center w-[800px] h-[600px] bg-slate-800 rounded-lg">
                <Loader2 className="w-12 h-12 animate-spin text-white" />
              </div>
            ) : (
              <img
                src={imageUrls[screenshots[selectedIndex]?.id] || ''}
                alt="Full screenshot"
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
              />
            )}
            <p className="text-center text-white mt-4 text-sm">
              {formatDate(screenshots[selectedIndex]?.captured_at)} • 
              {selectedIndex + 1} of {screenshots.length}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ScreenshotGallery;
